import {
    DobContextMenu,
    DobContextMenuItem,
    DobContextMenuSeparator,
    DobContextMenuSub,
    DobContextMenuSubTrigger,
    DobContextMenuSubContent,
} from '@dedalusdiit/dob-components';
import React from 'react';
import { observer } from 'mobx-react-lite';
import { t } from 'i18next';
import { useNavigate } from 'react-router';
import { InvoiceStatus } from '@/Components/Invoicing/InvoiceStatus';
import { OrderForWorklist } from '@/Models/GenericWorklist/WorklistResult';
import Styles from './ContextMenuWrapper.module.scss';
import { useSystemSettingsContext } from '@/Contexts/App/SystemSettingsContext';
import { useUserContext } from '@/Contexts/Authenticated/UserContext';
import { PopupProcedureReportInformation } from '@/Models/PopupProcedureReportInformation';
import { IWorklistStore } from '../../Stores/WorklistStore';
import { IWorklistSelectionHelper } from '@/Pages/GenericWorklist/Stores/WorklistSelectionHelper';
import { navigateToReport } from '@/Pages/GenericWorklist/Logic/ReportNavigation';

export interface ContextMenuWrapperProps {
    worklistStore: IWorklistStore;
    worklistSelectionHelper: IWorklistSelectionHelper;
}
const ContextMenuWrapper = observer((props: ContextMenuWrapperProps): React.ReactElement | null => {
    const navigate = useNavigate();
    const { user } = useUserContext();
    const { worklistStore, worklistSelectionHelper } = props;
    const selectedRow = worklistStore.selectedOrders[0];

    if (!selectedRow) {
        return null;
    }

    const { OrderInfo, PatientInfo } = selectedRow;

    const doNavigate = (url: string): void => {
        navigate(url)
            .catch((err) => {
                console.error('ContextMenuWrapper could not navigate', {
                    error: err,
                    url,
                    orderId: selectedRow.OrderInfo.OrderID,
                });
            });
    };

    return (
        <DobContextMenu>
            <DobContextMenuItem onSelect={() => doNavigate('/Patients/Detail/' + PatientInfo.ID)}>
                {t('Patient')}
            </DobContextMenuItem>
            <DobContextMenuItem onSelect={() => doNavigate('/Patients/OrderHistory/' + PatientInfo.ID)}>{t('OrderHistory')} </DobContextMenuItem>
            <DobContextMenuItem
                disabled={OrderInfo?.InvoiceStatus !== InvoiceStatus.CONFIRMED || !OrderInfo.InvoiceEID}
                onSelect={() => doNavigate('/Invoices/DetailConfirmed/' + OrderInfo.InvoiceEID)}
            >
                {t('Invoice')}
            </DobContextMenuItem>
            <DobContextMenuItem disabled={!OrderInfo?.VisitID} onSelect={() => doNavigate('/Visits/Detail/' + OrderInfo?.VisitID)}>{t('Visit')} </DobContextMenuItem>
            { user.canCreateAndEditReport
                && (
                    <ReportContextMenuItem
                        selectedRow={selectedRow}
                        worklistStore={worklistStore}
                    />
                ) }
            <DobContextMenuSeparator />
            <LabelContextMenuItem
                selectedRow={selectedRow}
                worklistStore={worklistStore}
                worklistSelectionHelper={worklistSelectionHelper}
            />
            <DobContextMenuItem
                disabled={OrderInfo.ReadyForInvoicing}
                onSelect={() => void worklistSelectionHelper.handleMarkAsReady()}
            >
                {t('MarkAsReadyForInvoicing')}
            </DobContextMenuItem>
        </DobContextMenu>
    );
});

interface LabelContextMenuItemProps {
    selectedRow: OrderForWorklist;
    worklistStore: IWorklistStore;
    worklistSelectionHelper: IWorklistSelectionHelper;
}

const LabelContextMenuItem = observer(({ selectedRow, worklistStore, worklistSelectionHelper }: LabelContextMenuItemProps): React.ReactElement => {
    const { availableLabels } = worklistStore;

    if (availableLabels.length <= 0) {
        return (
            <DobContextMenuItem disabled>
                {t('Label')}
            </DobContextMenuItem>
        );
    }

    return (
        <DobContextMenuSub>
            <DobContextMenuSubTrigger>{t('Label')}</DobContextMenuSubTrigger>
            <DobContextMenuSubContent sideOffset={6} className={Styles.scrollableContent}>
                {availableLabels.map((label) => {
                    const isApplied = worklistSelectionHelper.isLabelApplied(label.ID, selectedRow.OrderInfo?.OrderID);

                    return (
                        <DobContextMenuItem
                            key={label.ID}
                            onSelect={() => void (isApplied ? worklistSelectionHelper.removeLabelFromOrders(label.ID) : worklistSelectionHelper.assignLabelToOrders(label.ID))}
                            className={isApplied ? Styles.appliedLabel : Styles.unassignedLabel}
                        >
                            {label.Name}
                        </DobContextMenuItem>
                    );
                })}
            </DobContextMenuSubContent>
        </DobContextMenuSub>
    );
});

interface ReportContextMenuItemProps {
    selectedRow: OrderForWorklist;
    worklistStore: IWorklistStore;
}

const ReportContextMenuItem = observer(({ selectedRow, worklistStore }: ReportContextMenuItemProps): React.ReactElement | null => {
    const settingsContext = useSystemSettingsContext();
    const navigate = useNavigate();
    const reportController = settingsContext.getValue('ReportController')!;

    const orderReportId = selectedRow.OrderInfo?.ReportID ?? null;
    const procedureReports = selectedRow.OrderProceduresInfos?.filter((p) => p.ReportID) ?? [];

    if (!orderReportId && procedureReports.length === 0) {
        const handleReportClick = (): void => {
            void worklistStore.openReportDialog(selectedRow.OrderInfo.OrderID)
                .then((res: PopupProcedureReportInformation | null) => {
                    if (!res) {
                        return;
                    }

                    const procedures = res.Items.map((x) => x.OrderHasProcedure_UID);
                    navigateToReport(navigate, selectedRow.OrderInfo.OrderID, undefined, reportController, procedures);
                });
        };

        return (
            <DobContextMenuItem
                onSelect={handleReportClick}
            >
                {t('Report')}
            </DobContextMenuItem>
        );
    }

    if (orderReportId) {
        return (
            <DobContextMenuItem onSelect={() => navigateToReport(navigate, selectedRow.OrderInfo.OrderID, orderReportId, reportController)}>
                {t('Report')}
            </DobContextMenuItem>
        );
    }

    const distinctReportIds = Array.from(new Set(procedureReports.map((x) => x.ReportID)));
    if (distinctReportIds.length === 1) {
        return (
            <DobContextMenuItem onSelect={() => navigateToReport(navigate, selectedRow.OrderInfo.OrderID, procedureReports[0].ReportID ?? undefined, reportController, procedureReports.map((x) => x.OrderProcedureID))}>
                {t('Report')}
            </DobContextMenuItem>
        );
    }

    return (
        <DobContextMenuSub>
            <DobContextMenuSubTrigger>{t('Report')}</DobContextMenuSubTrigger>
            <DobContextMenuSubContent sideOffset={6} className={Styles.scrollableContent}>
                {procedureReports.map((proc) => (
                    <DobContextMenuItem
                        key={proc.OrderProcedureID}
                        onSelect={() => navigateToReport(navigate, selectedRow.OrderInfo.OrderID, proc.ReportID ?? undefined, reportController, [proc.OrderProcedureID])}
                    >
                        {proc.ProcedureInfo?.ModalityName} {proc.ProcedureInfo?.Code}
                    </DobContextMenuItem>
                ))}
            </DobContextMenuSubContent>
        </DobContextMenuSub>
    );
});

export default ContextMenuWrapper;
